"use client";
import { useCallback, useEffect, useState } from "react";
import {
  Plus,
  ArrowRight,
  Users,
  CalendarDays,
  Cloud,
  CheckCircle2,
  KeyRound,
  Copy,
  PenLine,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import type { Item } from "./workspace";

type OpData = {
  status: string;
  owner: string;
  role: string;
  department: string;
  date: string;
  priority: string;
  body: string;
  response: string;
};
type Op = {
  id?: string;
  scope: string;
  kind: string;
  title: string;
  data: OpData;
};
type Chamber = { id: string; name: string; quota: number; used: number };
const defaults: OpData = {
  status: "open",
  owner: "",
  role: "",
  department: "Chamber",
  date: "",
  priority: "normal",
  body: "",
  response: "",
};
const labels: Record<string, string> = {
  team: "Chamber team",
  staff: "Our people",
  onboarding: "Chamber onboarding",
  work: "Office work",
  support: "Service desk",
  storage: "Storage allocations",
  access: "Access & roles",
};
const details: Record<string, string> = {
  team: "Define responsibilities, maintain your chamber roster and keep every handover clear.",
  staff:
    "Organise the people behind Digital Chambers, with an accountable owner for each function.",
  onboarding:
    "Track each chamber from first conversation through document preparation and launch readiness.",
  work: "One shared queue for operations, client success, finance and technology.",
  support:
    "A direct working channel between advocates and the Digital Chambers office.",
  storage: "Your Google Drive pool. A measured allowance for every chamber.",
  access: "Issue, suspend and rotate time-limited chamber credentials. Passwords are shown once and never stored in readable form.",
};
async function request<T>(
  path: string,
  value?: unknown,
  method = "POST",
): Promise<T> {
  const r = await fetch(
    "/api/" + path,
    value
      ? {
          method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(value),
        }
      : undefined,
  );
  const data = (await r.json()) as T & { error?: string };
  if (!r.ok) throw Error(data.error || "Unable to save. Please try again.");
  return data;
}

type AccessRow = { login_id: string; chamber_id: string; name: string; role: string; active: boolean; expiresAt: number; last_sign_in: string | null };
const accessRoles = ["principal_advocate", "advocate", "associate", "chamber_coordinator", "clerk", "researcher", "drafting_assistant", "read_only_reviewer"];

function AccessAdministration({ chambers }: { chambers: Chamber[] }) {
  const [items, setItems] = useState<AccessRow[]>([]), [error, setError] = useState(""), [busy, setBusy] = useState(false), [issued, setIssued] = useState<{ login_id: string; password: string; expiresAt: number } | null>(null), [creating, setCreating] = useState(false);
  const refresh = useCallback(async () => setItems((await request<{ items: AccessRow[] }>("office/access")).items), []);
  useEffect(() => { void Promise.resolve().then(refresh).catch(e => setError(e.message)); }, [refresh]);
  async function act(id: string, action: "reissue" | "suspend" | "activate") {
    setBusy(true); setError("");
    try { const result = await request<{ access?: { login_id: string; password: string; expiresAt: number } }>(`office/access/${encodeURIComponent(id)}`, { action }, "PATCH"); if (result.access) setIssued(result.access); await refresh(); }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }
  return <div className="mg-root">
    <div className="mg-heading"><div><p className="dc-eyebrow">CHATAKE INNOWORKS · AUTHENTICATED OFFICE</p><h1>Access & roles</h1><p>{details.access}</p></div><button className="ws-button" onClick={() => setCreating(true)}><KeyRound size={16}/>Issue access</button></div>
    {error && <p className="ws-error" role="alert">{error}</p>}
    <div className="mg-structure">{[["Principal advocate","Final chamber authority"],["Advocate / associate","Legal work and preparation"],["Coordinator / clerk","Clients, dates and operations"],["Research / drafting","Focused working access"],["Read-only reviewer","Inspection without changes"]].map(([title,body]) => <div key={title}><Users size={19}/><h3>{title}</h3><p>{body}</p></div>)}</div>
    <p className="mg-note">Temporary access is for the founding pilot. The Administration Office remains protected by Cognito password and authenticator MFA.</p>
    <section className="ws-panel mg-panel"><h2>Issued chamber access</h2>{items.length === 0 ? <p>No temporary credentials have been issued.</p> : items.map(row => <div className="mg-quota" key={row.login_id}><div><b>{row.name}</b><small>{row.login_id} · {row.role.replaceAll("_", " ")} · {chambers.find(c => c.id === row.chamber_id)?.name ?? row.chamber_id}</small></div><div><b>{row.active ? "Active" : "Suspended"}</b><small>Expires {new Date(row.expiresAt * 1000).toLocaleDateString("en-IN")} · {row.last_sign_in ? `last used ${new Date(row.last_sign_in).toLocaleString("en-IN")}` : "unused"}</small></div><div className="mg-access-actions"><button disabled={busy} onClick={() => act(row.login_id, "reissue")}>Reissue</button><button disabled={busy} onClick={() => act(row.login_id, row.active ? "suspend" : "activate")}>{row.active ? "Suspend" : "Activate"}</button></div></div>)}</section>
    <Dialog open={creating} onOpenChange={setCreating}><DialogContent className="ws-dialog"><DialogTitle>Issue temporary chamber access</DialogTitle><DialogDescription>The password appears once after creation.</DialogDescription><form className="ws-form" onSubmit={async e => { e.preventDefault(); setBusy(true); setError(""); const f = new FormData(e.currentTarget); try { const result = await request<{ access: { login_id: string; password: string; expiresAt: number } }>("office/access", { chamber_id: f.get("chamber_id"), login_id: f.get("login_id"), name: f.get("name"), role: f.get("role"), expires_days: Number(f.get("expires_days")) }); setIssued(result.access); setCreating(false); await refresh(); } catch(e) { setError((e as Error).message); } finally { setBusy(false); } }}><label>Chamber<select name="chamber_id" required>{chambers.map(c => <option value={c.id} key={c.id}>{c.name}</option>)}</select></label><label>Full name<input name="name" required minLength={2} maxLength={120}/></label><label>Login ID<input name="login_id" required pattern="[a-z0-9][a-z0-9._-]{2,63}" placeholder="mahesh.jagtap"/></label><label>Role<select name="role">{accessRoles.map(role => <option key={role} value={role}>{role.replaceAll("_", " ")}</option>)}</select></label><label>Expires after<select name="expires_days" defaultValue="14"><option value="7">7 days</option><option value="14">14 days</option><option value="30">30 days</option></select></label><button className="ws-button" disabled={busy}>{busy ? "Issuing…" : "Issue one-time password"}</button></form></DialogContent></Dialog>
    <Dialog open={!!issued} onOpenChange={open => !open && setIssued(null)}><DialogContent className="ws-dialog"><DialogTitle>Copy the temporary credential now</DialogTitle><DialogDescription>The password cannot be retrieved after this window closes. Reissue it if it is lost.</DialogDescription>{issued && <div className="mg-credential"><label>Login ID<code>{issued.login_id}</code></label><label>Temporary password<code>{issued.password}</code></label><label>Expiry<code>{new Date(issued.expiresAt * 1000).toLocaleString("en-IN")}</code></label><button className="ws-button" onClick={() => navigator.clipboard.writeText(`Login ID: ${issued.login_id}\nTemporary password: ${issued.password}\nSign in: https://chambers.mindforgeai.co.in/sign-in`)}><Copy size={15}/>Copy access details</button></div>}</DialogContent></Dialog>
  </div>;
}
const gb = (n: number) =>
  (n / 1e9).toLocaleString("en-IN", { maximumFractionDigits: 2 }) + " GB";
const statusLabel = (s: string) => s.replaceAll("_", " ");
export function Management({
  view,
  office,
  chamberId,
  chambers = [],
  onRefresh,
}: {
  view: string;
  office: boolean;
  chamberId: string;
  chambers?: Chamber[];
  onRefresh: () => Promise<void>;
}) {
  const [rows, setRows] = useState<Op[]>([]),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [editing, setEditing] = useState<Op | null>(null),
    [busy, setBusy] = useState(false),
    [search, setSearch] = useState(""),
    [showArchive, setShowArchive] = useState(false);
  const refresh = useCallback(async () => {
    const d = await request<{ items: Op[] }>("management");
    setRows(d.items);
  }, []);
  useEffect(() => {
    Promise.resolve()
      .then(refresh)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [refresh]);
  const create = () => {
    setError("");
    setEditing({
      scope: office
        ? ["staff", "work"].includes(view)
          ? "office"
          : (chambers[0]?.id ?? "")
        : chamberId,
      kind: view,
      title: "",
      data: { ...defaults, department: office ? "Operations" : "Chamber" },
    });
  };
  const visible = rows.filter(
    (r) =>
      r.kind === view &&
      (showArchive
        ? r.data.status === "archived"
        : r.data.status !== "archived") &&
      [r.title, r.data.owner, r.data.role, r.data.department]
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  if (view === "access") return <AccessAdministration chambers={chambers} />;
  return (
    <div className="mg-root">
      <div className="mg-heading">
        <div>
          <p className="dc-eyebrow">
            {office
              ? "CHATAKE INNOWORKS · OFFICE"
              : "YOUR CHAMBER · OPERATIONS"}
          </p>
          <h1>{labels[view]}</h1>
          <p>{details[view]}</p>
        </div>
        {view !== "storage" && (
          <button className="ws-button" onClick={create}>
            <Plus size={16} />
            {view === "team" || view === "staff"
              ? "Add person"
              : view === "support"
                ? "New request"
                : view === "onboarding"
                  ? "Start onboarding"
                  : "New work item"}
          </button>
        )}
      </div>
      {error && (
        <p className="ws-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="ws-notice" role="status">
          {notice}
        </p>
      )}
      {view === "storage" ? (
        <>
          <div className="mg-pool">
            <div>
              <Cloud size={26} />
              <p className="dc-eyebrow">COMPANY GOOGLE DRIVE</p>
              <h2>
                5 TB <em>shared foundation.</em>
              </h2>
              <p>
                5 GB per chamber, allocated and enforced by Digital Chambers.
              </p>
              <span className="mg-badge">
                Connection pending · owner-declared capacity
              </span>
            </div>
            <div>
              <strong>{gb(chambers.reduce((n, c) => n + c.quota, 0))}</strong>
              <p>Currently allocated in the local plan</p>
              <progress
                aria-label="Planned storage allocations"
                max={4000}
                value={chambers.reduce((n, c) => n + c.quota, 0) / 1e9}
              />
              <p>4 TB allocation budget · 1 TB planning reserve</p>
              <small>
                Actual Drive free space and existing account usage have not been
                read. Local file usage below is separate.
              </small>
            </div>
          </div>
          <section className="ws-panel mg-panel">
            <h2>Chamber allowances</h2>
            <p>
              Adjust allocations from 1–50 GB. The server rejects an allowance
              below retained file usage or above the planning budget.
            </p>
            {chambers.map((c) => (
              <form
                className="mg-quota"
                key={c.id}
                onSubmit={async (e) => {
                  e.preventDefault();
                  const f = new FormData(e.currentTarget);
                  setBusy(true);
                  setError("");
                  try {
                    await request(
                      "office/" + c.id,
                      { quota: Number(f.get("quota")) * 1e9 },
                      "PATCH",
                    );
                    await onRefresh();
                    setNotice("Storage allowance updated.");
                  } catch (e) {
                    setError((e as Error).message);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <div>
                  <b>{c.name}</b>
                  <small>
                    {gb(c.used)} retained locally · {gb(c.quota)} allowance
                  </small>
                </div>
                <label>
                  Allowance (GB)
                  <input
                    name="quota"
                    type="number"
                    min="1"
                    max="50"
                    step="1"
                    defaultValue={c.quota / 1e9}
                    required
                  />
                </label>
                <button className="ws-button ws-outline" disabled={busy}>
                  Save allowance
                </button>
              </form>
            ))}
          </section>
        </>
      ) : (
        <>
          {(view === "team" || view === "staff") && (
            <div className="mg-structure">
              {(office
                ? [
                    ["Leadership", "Policy, approvals and escalation"],
                    ["Client success", "Onboarding, support and renewals"],
                    ["Operations", "Records, storage and quality"],
                    ["Technology", "Access, integrations and reliability"],
                  ]
                : [
                    ["Principal advocate", "Owns the chamber and final review"],
                    ["Associates", "Research, drafting and preparation"],
                    [
                      "Clerk / coordinator",
                      "Dates, filing and document organisation",
                    ],
                  ]
              ).map(([title, body]) => (
                <div key={title}>
                  <Users size={19} />
                  <h3>{title}</h3>
                  <p>{body}</p>
                </div>
              ))}
            </div>
          )}
          {view === "onboarding" && (
            <div className="mg-steps">
              {[
                "Enquiry & owner",
                "Practice setup",
                "Archive preparation",
                "Access & training",
                "Launch review",
              ].map((s, i) => (
                <span key={s}>
                  <b>0{i + 1}</b>
                  {s}
                </span>
              ))}
            </div>
          )}
          <div className="mg-toolbar">
            <label>
              Find {labels[view]?.toLowerCase()}
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search names or responsibility…"
              />
            </label>
            <label className="mg-check">
              <input
                type="checkbox"
                checked={showArchive}
                onChange={(e) => setShowArchive(e.target.checked)}
              />
              Archived
            </label>
            <span>{visible.length} records</span>
          </div>
          {(view === "team" || view === "staff") && (
            <p className="mg-note">
              This is a responsibility roster. Adding a person does not create a
              login or grant access to private matters.
            </p>
          )}
          {loading ? (
            <p role="status">Loading your records…</p>
          ) : visible.length === 0 ? (
            <section className="ws-panel mg-empty">
              <CheckCircle2 size={28} />
              <h2>
                {showArchive
                  ? "No archived records."
                  : "A clear place to begin."}
              </h2>
              <p>
                {showArchive
                  ? "Archived items appear here."
                  : "Add the first record to give this work an owner and a next step."}
              </p>
              {!showArchive && (
                <button className="ws-button ws-outline" onClick={create}>
                  Create a record <ArrowRight size={15} />
                </button>
              )}
            </section>
          ) : (
            <div className="mg-cards">
              {visible.map((r) => (
                <button
                  className="mg-card"
                  key={r.id}
                  onClick={() => {
                    setError("");
                    setEditing(r);
                  }}
                >
                  <div>
                    <span className={"mg-badge mg-status-" + r.data.status}>
                      {statusLabel(r.data.status)}
                    </span>
                    <span>
                      {r.data.priority !== "normal" ? r.data.priority : ""}
                    </span>
                  </div>
                  <h2>{r.title}</h2>
                  <p>
                    {r.data.role || r.data.department}
                    {r.data.owner ? " · " + r.data.owner : ""}
                  </p>
                  {office && r.scope !== "office" && (
                    <small>
                      {chambers.find((c) => c.id === r.scope)?.name ??
                        "Chamber"}
                    </small>
                  )}
                  <p className="mg-excerpt">
                    {r.data.body ||
                      "Add responsibilities, a handover or the next step."}
                  </p>
                  <footer>
                    <span>{r.data.date || "No due date"}</span>
                    <span>
                      {r.data.response
                        ? "Office response available"
                        : "Open record"}{" "}
                      <ArrowRight size={14} />
                    </span>
                  </footer>
                </button>
              ))}
            </div>
          )}
        </>
      )}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="ws-dialog">
          <DialogTitle>
            {editing?.id ? "Update" : "New"}{" "}
            {view === "team" || view === "staff"
              ? "person"
              : view === "support"
                ? "service request"
                : "record"}
          </DialogTitle>
          <DialogDescription>
            {view === "support"
              ? "This request and the office response are shared with the platform office. Keep legal documents in your private chamber."
              : "Saved in this private workspace. No invitation or external message is sent."}
          </DialogDescription>
          {error && (
            <p className="ws-error" role="alert">
              {error}
            </p>
          )}
          {editing && (
            <form
              className="ws-form"
              onSubmit={async (e) => {
                e.preventDefault();
                const f = new FormData(e.currentTarget);
                const data = { ...editing.data };
                for (const k of Object.keys(defaults) as (keyof OpData)[])
                  if (f.has(k)) data[k] = String(f.get(k));
                setBusy(true);
                setError("");
                try {
                  await request(
                    "management" + (editing.id ? "/" + editing.id : ""),
                    {
                      kind: editing.kind,
                      scope: String(f.get("scope") ?? editing.scope),
                      title: String(f.get("title")),
                      data,
                    },
                    editing.id ? "PATCH" : "POST",
                  );
                  await refresh();
                  setEditing(null);
                  setNotice("Saved. Your management records are up to date.");
                } catch (e) {
                  setError((e as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              <label>
                {view === "team" || view === "staff" ? "Full name" : "Title"}
                <input
                  name="title"
                  required
                  minLength={2}
                  maxLength={160}
                  defaultValue={editing.title}
                />
              </label>
              {office &&
                ["onboarding", "support"].includes(view) &&
                !editing.id && (
                  <label>
                    Chamber
                    <select name="scope" required defaultValue={editing.scope}>
                      <option value="">Select a chamber</option>
                      {chambers.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              <div className="ws-form-grid">
                <label>
                  {view === "team" || view === "staff"
                    ? "Role / responsibility"
                    : "Stage / workstream"}
                  <input
                    name="role"
                    maxLength={120}
                    defaultValue={editing.data.role}
                    placeholder={
                      view === "onboarding" ? "e.g. Archive preparation" : ""
                    }
                  />
                </label>
                <label>
                  Accountable owner
                  <input
                    name="owner"
                    maxLength={120}
                    defaultValue={editing.data.owner}
                  />
                </label>
              </div>
              {office && (
                <label>
                  Department
                  <select
                    name="department"
                    defaultValue={editing.data.department}
                  >
                    {[
                      "Chamber",
                      "Client success",
                      "Operations",
                      "Finance",
                      "Technology",
                      "Leadership",
                    ].map((d) => (
                      <option key={d}>{d}</option>
                    ))}
                  </select>
                </label>
              )}
              <div className="ws-form-grid">
                {(office || view !== "support") && (
                  <label>
                    Status
                    <select name="status" defaultValue={editing.data.status}>
                      {[
                        "open",
                        "in_progress",
                        "waiting",
                        "done",
                        "archived",
                      ].map((s) => (
                        <option key={s} value={s}>
                          {statusLabel(s)}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label>
                  Next review / due date
                  <input
                    type="date"
                    name="date"
                    defaultValue={editing.data.date}
                  />
                </label>
              </div>
              <label>
                Priority
                <select name="priority" defaultValue={editing.data.priority}>
                  {["normal", "high", "urgent"].map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </label>
              <label>
                {view === "team" || view === "staff"
                  ? "Responsibilities & handover"
                  : "Details & next action"}
                <textarea
                  name="body"
                  rows={4}
                  maxLength={6000}
                  defaultValue={editing.data.body}
                />
              </label>
              {view === "support" &&
                (office ? (
                  <label>
                    Response to advocate
                    <textarea
                      name="response"
                      rows={3}
                      maxLength={6000}
                      defaultValue={editing.data.response}
                    />
                  </label>
                ) : (
                  <div className="mg-reply">
                    <b>Office response</b>
                    <p>
                      {editing.data.response ||
                        "Awaiting a response from the office."}
                    </p>
                    <small>Status: {statusLabel(editing.data.status)}</small>
                  </div>
                ))}
              <button className="ws-button" disabled={busy}>
                {busy ? "Saving…" : "Save record"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
export function CourtDiary({
  items,
  onOpen,
  onEdit,
  onAdd,
}: {
  items: Item[];
  onOpen: (item: Item) => void;
  onEdit: (item: Item) => void;
  onAdd: () => void;
}) {
  const [month, setMonth] = useState(() => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 7));
  const entries = items
    .filter(
      (i) =>
        !i.deleted &&
        ["matter", "event", "task"].includes(i.kind) &&
        i.data.date?.startsWith(month) &&
        i.data.status !== "done" &&
        i.data.status !== "closed",
    )
    .sort((a, b) => (a.data.date ?? "").localeCompare(b.data.date ?? ""));
  return (
    <div className="mg-root">
      <div className="mg-heading">
        <div>
          <p className="dc-eyebrow">HEARINGS · FILING · FOLLOW-UPS</p>
          <h1>Your court diary.</h1>
          <p>
            All matter hearings, timeline dates and open tasks in one working
            agenda.
          </p>
        </div>
        <div className="mg-diary-actions"><label>
          Month
          <input
            aria-label="Diary month"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </label><button className="ws-button" onClick={onAdd}><Plus size={16}/>Add diary entry</button></div>
      </div>
      <section className="ws-panel mg-panel">
        {entries.length ? (
          entries.map((i) => (
            <div className="mg-agenda" key={i.id}>
            <button onClick={() => onOpen(i)} aria-label={`Open ${i.title}`}>
              <span>
                <CalendarDays size={19} />
                {i.data.date}
              </span>
              <div>
                <b>{i.title}</b>
                <small>
                  {i.kind === "matter"
                    ? "Hearing"
                    : i.kind === "task"
                      ? "Task deadline"
                      : "Timeline event"}{" "}
                  · {i.data.court || i.data.priority || "Chamber"}
                </small>
              </div>
              <ArrowRight size={17} />
            </button><button className="mg-agenda-edit" onClick={() => onEdit(i)} aria-label={`Edit ${i.title}`}><PenLine size={16}/>Edit</button>
            </div>
          ))
        ) : (
          <div className="mg-empty">
            <h2>A clear month.</h2>
            <p>
              Add a hearing date to a matter, or date a task or timeline event,
              to see it here.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
